/**
 * Travel 日期／數字的語系格式化（依 app locale，不依瀏覽器預設語系）。
 * - 純日期（yyyy-MM-dd 或 [y, m, d]）以本地年月日建立，不經 UTC 解析，避免跨日。
 * - 無 timezone 的日期時間依字面值顯示，不轉換時區；帶 Z／offset 者才由 Intl 依裝置時區呈現。
 */

const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;

/** zh_TW → zh-TW；空值回傳 undefined 交由 Intl 決定。 */
export function toIntlLocale(locale) {
  if (!locale) return undefined;
  return String(locale).replace(/_/g, '-');
}

function validDate(date) {
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

/** 解析純日期為本地日期（不跨日）。無法解析時回傳 null。 */
export function parsePlainDate(value) {
  if (!value) return null;
  if (Array.isArray(value)) {
    if (value.length < 3) return null;
    return validDate(new Date(Number(value[0]), Number(value[1]) - 1, Number(value[2])));
  }
  const match = PLAIN_DATE.exec(String(value).slice(0, 10));
  if (!match) return null;
  return validDate(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

function safeFormat(intlLocale, options, date) {
  try {
    return new Intl.DateTimeFormat(intlLocale, options).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, options).format(date);
  }
}

export function formatPlainDate(value, intlLocale) {
  const date = parsePlainDate(value);
  return date ? safeFormat(intlLocale, { dateStyle: 'medium' }, date) : '';
}

/** 純日期區間；支援 formatRange 時由 Intl 決定連接符與省略（如同月合併）。 */
export function formatPlainDateRange(start, end, intlLocale) {
  const from = parsePlainDate(start);
  const to = parsePlainDate(end);
  if (!from || !to) return '';
  try {
    const formatter = new Intl.DateTimeFormat(intlLocale, { dateStyle: 'medium' });
    if (typeof formatter.formatRange === 'function') return formatter.formatRange(from, to);
    return `${formatter.format(from)} – ${formatter.format(to)}`;
  } catch {
    return `${formatPlainDate(start)} – ${formatPlainDate(end)}`;
  }
}

/** 日期時間：無 timezone 依字面值（本地欄位）呈現；帶 timezone 由 Intl 呈現。無法解析回傳空字串。 */
export function formatDateTimeValue(value, intlLocale) {
  if (!value) return '';
  const raw = String(value);
  const local = LOCAL_DATE_TIME.exec(raw);
  const date = local
    ? validDate(new Date(
      Number(local[1]), Number(local[2]) - 1, Number(local[3]),
      Number(local[4]), Number(local[5]), Number(local[6] || 0),
    ))
    : validDate(new Date(raw));
  return date ? safeFormat(intlLocale, { dateStyle: 'medium', timeStyle: 'short' }, date) : '';
}

export function formatCount(value, intlLocale) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  try {
    return new Intl.NumberFormat(intlLocale).format(number);
  } catch {
    return String(number);
  }
}
